import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const SERVICE_TYPES = [
  "Boiler Installation",
  "Gas Safety Inspections",
  "Commercial Heating Systems",
  "Pipework",
  "Maintenance Contracts",
  "Emergency Repairs",
  "Ventilation Systems",
  "Gas Appliance Servicing",
];

const LEAD_SOURCES = [
  { value: "website", label: "Website" },
  { value: "phone", label: "Phone" },
  { value: "referral", label: "Referral" },
  { value: "walk-in", label: "Walk-in" },
  { value: "email", label: "Email" },
  { value: "trade_show", label: "Trade Show" },
  { value: "social_media", label: "Social Media" },
];

const STAGES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "proposal", label: "Proposal" },
  { value: "negotiation", label: "Negotiation" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const emptyForm = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  service_type: "Boiler Installation",
  lead_source: "website",
  stage: "new",
  estimated_value: "",
  priority: "medium",
  address: "",
  description: "",
};

export const LeadForm = ({ open, onOpenChange, onSubmit, initialData }) => {
  const [form, setForm] = useState(emptyForm);
  const isEdit = !!initialData;

  useEffect(() => {
    if (initialData) {
      setForm({
        company_name: initialData.company_name || "",
        contact_name: initialData.contact_name || "",
        email: initialData.email || "",
        phone: initialData.phone || "",
        service_type: initialData.service_type || "Boiler Installation",
        lead_source: initialData.lead_source || "website",
        stage: initialData.stage || "new",
        estimated_value: initialData.estimated_value?.toString() || "",
        priority: initialData.priority || "medium",
        address: initialData.address || "",
        description: initialData.description || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [initialData, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.contact_name.trim()) {
      toast.error("Company name and contact name are required");
      return;
    }
    onSubmit({
      ...form,
      estimated_value: parseFloat(form.estimated_value) || 0,
    });
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[580px] bg-card border-white/10 backdrop-blur-xl" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {isEdit ? "EDIT LEAD" : "NEW LEAD"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            {isEdit ? "Update the lead details below." : "Fill in the details to create a new lead."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Company Name *</Label>
              <Input
                data-testid="lead-form-company"
                value={form.company_name}
                onChange={e => updateField("company_name", e.target.value)}
                className="h-10 bg-secondary/50 border-white/5 focus:ring-2 focus:ring-blue-500/50 rounded-sm"
                placeholder="Acme Industrial Ltd"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Contact Name *</Label>
              <Input
                data-testid="lead-form-contact"
                value={form.contact_name}
                onChange={e => updateField("contact_name", e.target.value)}
                className="h-10 bg-secondary/50 border-white/5 focus:ring-2 focus:ring-blue-500/50 rounded-sm"
                placeholder="John Smith"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Email</Label>
              <Input
                data-testid="lead-form-email"
                type="email"
                value={form.email}
                onChange={e => updateField("email", e.target.value)}
                className="h-10 bg-secondary/50 border-white/5 focus:ring-2 focus:ring-blue-500/50 rounded-sm"
                placeholder="john@acme.co.uk"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Phone</Label>
              <Input
                data-testid="lead-form-phone"
                value={form.phone}
                onChange={e => updateField("phone", e.target.value)}
                className="h-10 bg-secondary/50 border-white/5 focus:ring-2 focus:ring-blue-500/50 rounded-sm"
                placeholder="+44 20 7946 0958"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Service Type</Label>
              <Select value={form.service_type} onValueChange={v => updateField("service_type", v)}>
                <SelectTrigger data-testid="lead-form-service" className="h-10 bg-secondary/50 border-white/5 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Lead Source</Label>
              <Select value={form.lead_source} onValueChange={v => updateField("lead_source", v)}>
                <SelectTrigger data-testid="lead-form-source" className="h-10 bg-secondary/50 border-white/5 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Stage</Label>
              <Select value={form.stage} onValueChange={v => updateField("stage", v)}>
                <SelectTrigger data-testid="lead-form-stage" className="h-10 bg-secondary/50 border-white/5 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Estimated Value (GBP)</Label>
              <Input
                data-testid="lead-form-value"
                type="number"
                value={form.estimated_value}
                onChange={e => updateField("estimated_value", e.target.value)}
                className="h-10 bg-secondary/50 border-white/5 focus:ring-2 focus:ring-blue-500/50 rounded-sm"
                placeholder="15000"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Priority</Label>
              <Select value={form.priority} onValueChange={v => updateField("priority", v)}>
                <SelectTrigger data-testid="lead-form-priority" className="h-10 bg-secondary/50 border-white/5 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs tracking-wider uppercase text-muted-foreground">Address</Label>
            <Input
              data-testid="lead-form-address"
              value={form.address}
              onChange={e => updateField("address", e.target.value)}
              className="h-10 bg-secondary/50 border-white/5 focus:ring-2 focus:ring-blue-500/50 rounded-sm"
              placeholder="123 Industrial Estate, London"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs tracking-wider uppercase text-muted-foreground">Description</Label>
            <Textarea
              data-testid="lead-form-description"
              value={form.description}
              onChange={e => updateField("description", e.target.value)}
              className="bg-secondary/50 border-white/5 focus:ring-2 focus:ring-blue-500/50 rounded-sm min-h-[70px]"
              placeholder="Brief description of the enquiry..."
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm border-white/10 uppercase tracking-wider text-xs"
              data-testid="lead-form-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs"
              data-testid="lead-form-submit"
            >
              {isEdit ? "Update Lead" : "Create Lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
